import { useState, ReactNode } from 'react';
import classNames from 'classnames';
import { TabItem, TabItemProps } from '@components/nav/TabItem';
import './TabList.scss';

export interface TabListProps {
  /** id to reference the element */
  id?: string;
  /** optional default active tab, will use first tab if not provided */
  defaultActiveKey?: string;
  /** Optional class */
  className?: string;
  /** Animation to be used . Default Fade */
  animationClass?: 'fade' | 'none';
  /** Callback fired when the state is changed */
  onChange?: (key: string) => void;
  /** Array with tabItem and content details */
  tabs: (TabItemProps & { content: ReactNode })[];
}
/**
 * TabList
 * @param id - Used for identify the element.
 * @param defaultActiveKey - Optional default active tab, will use first tab if not provided.
 * @param className - Optional class.
 * @param animationClass - Animation to be used . Default Fade.
 * @param onChange - Callback fired when the state is changed.
 * @param tabs - Array with tabItem and content details.
 */
export const TabList: React.FC<TabListProps> = ({
  id,
  className,
  defaultActiveKey,
  animationClass = 'fade',
  onChange,
  tabs,
}) => {
  const [currentTabKey, setCurrentTabKey] = useState(defaultActiveKey || tabs[0].tabKey);

  const handleTabClick = (key: string, disabled?: boolean) => {
    if (disabled) return;
    setCurrentTabKey(key);
    onChange?.(key);
  };

  const handleKeyDown = (event: React.KeyboardEvent, key: string, disabled?: boolean) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTabClick(key);
    }
  };

  return (
    <div id={id} className={classNames('vds-tab-list', className)}>
      <div role="tablist" className="vds-tab-list__header">
        {tabs.map(tab => (
          <div
            key={`tab-${tab.tabKey}`}
            className="vds-tab-list__item"
            onClick={() => handleTabClick(tab.tabKey, tab.state === 'disabled')}
            onKeyDown={event => handleKeyDown(event, tab.tabKey, tab.state === 'disabled')}>
            <TabItem
              {...tab}
              state={
                currentTabKey === tab.tabKey && tab.state !== 'disabled' ? 'active' : tab.state
              }
              role="tab"
              aria-controls={`panel-${tab.id}`}
              aria-selected={currentTabKey === tab.tabKey}
              aria-disabled={tab.state === 'disabled'}
            />
          </div>
        ))}
      </div>
      <div className="vds-tab-list__body">
        {tabs.map(tab => (
          <div
            id={`panel-${tab.id}`}
            key={`content-${tab.tabKey}`}
            aria-labelledby={tab.id}
            role="tabpanel"
            className={classNames('vds-tab-list__content', {
              'vds-tab-list__content--active': currentTabKey === tab.tabKey,
              'vds-tab-list__content--fade': 'fade' === animationClass,
            })}>
            {tab.tabKey === currentTabKey ? tab.content : null}
          </div>
        ))}
      </div>
    </div>
  );
};
